import { Modal } from 'canoe';
import { FormikHelpers, FormikProvider, useFormik, useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import { SelectorForm } from '@candu-ai/placement/content-placements';
import { ContentPlacement } from '@candu-ai/placement/types';

interface Props {
  onClose: VoidFunction;
  onSubmit: (
    values: ContentPlacement,
    formikHelpers: FormikHelpers<ContentPlacement>,
  ) => Promise<void>;
  placementExists: boolean;
}

export const EditPlacementModal = ({ onClose, onSubmit, placementExists }: Props) => {
  const { values: placementTabValues, validationSchema, validateForm, isValid } = useFormikContext<
    ContentPlacement
  >();

  const formik = useFormik<ContentPlacement>({
    initialValues: placementTabValues,
    validationSchema,
    onSubmit: (values, formikHelpers) => {
      if ((!placementExists || formik.dirty) && isValid) {
        // if the form has changed, or it's a new placement, and is valid, submit it
        onSubmit(values, formikHelpers).then(() => onClose());
      } else if (isValid) {
        // if the form has not changed and is valid, just close the modal
        onClose();
      }
    },
  });

  const validateNestedForm = () => {
    validateForm(formik.values).then((err) => formik.setErrors(err));
  };

  useEffect(() => {
    validateNestedForm();
  }, [formik.isValidating]);

  return (
    <FormikProvider value={formik}>
      <Modal onClose={onClose} modalWidth="large" closeOnClickOutside>
        <Modal.Header title={placementExists ? 'Edit Placement' : 'Add Placement'} />
        <Modal.Body>
          <SelectorForm actionButtons={<></>} formikContext={formik} />
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            children: 'Cancel',
            onClick: () => {
              formik.resetForm({ values: placementTabValues });
              onClose();
            },
            'data-cy': 'cancel-edit',
          }}
          primaryAction={{
            children: placementExists ? 'Update' : 'Save',
            variant: 'secondary',
            onClick: () => {
              formik.submitForm();
            },
            'data-cy': 'save-placement',
          }}
        />
      </Modal>
    </FormikProvider>
  );
};
